import { useContext, useMemo, useRef } from "react";
import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { AuthContext } from "../Auth/AuthContext";
import useLogger from "../Logger/useLogger";
import { AuthState } from "../types/AuthProvider";
import {
  getAuthHeaders,
  handleAuthError,
  shouldSkipErrorHandling,
} from "./utils/authUtils";
import { apiConfig } from "./config";

/**
 * Configuration for the useAxiosClient hook
 */
export interface UseAxiosClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
  domain?: string;
  targetAccountUuid?: string;
}

/**
 * Custom hook that creates an axios instance with authentication
 *
 * Automatically adds:
 * - JWT Bearer token (from AWS Cognito)
 * - X-Account header (from AuthContext for multi-tenancy)
 * - X-Account header (from impersonation context if active)
 * - x-impersonate: "true" header (when impersonation context is active)
 *
 * Error handling matches GraphQL Apollo Client:
 * - 401/403: Automatic logout or stop impersonation
 * - Logging: Integrated with Logger service
 * - Environment-aware: Skips certain errors in dev/localhost
 *
 * @param config - Configuration object with baseURL and optional settings
 * @returns {AxiosInstance} Axios instance with JWT and X-Account authentication
 */
const useAxiosClient = (config: UseAxiosClientConfig = {}): AxiosInstance => {
  const { authState, logout, stopImpersonation } = useContext(AuthContext);
  const { logWarning, logError } = useLogger();
  // AuthContext is a js file so no inferred type
  const authRef = useRef<AuthState>(authState as AuthState);
  authRef.current = authState as AuthState;

  // Use provided baseURL or default to apiConfig.REST_API_URL
  const baseURL = config.baseURL || apiConfig.REST_API_URL;

  // Serialize headers to avoid unnecessary re-renders
  const headersString = useMemo(
    () => (config.headers ? JSON.stringify(config.headers) : ""),
    [config.headers]
  );

  const client = useMemo(() => {
    const resolvedBaseURL = config.domain
      ? new URL(config.domain, baseURL).href
      : baseURL;
    const axiosInstance = axios.create({
      baseURL: resolvedBaseURL,
      timeout: config.timeout || 30000,
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
    });

    // Request interceptor - adds JWT and X-Account automatically
    axiosInstance.interceptors.request.use(
      async (
        requestConfig: InternalAxiosRequestConfig
      ): Promise<InternalAxiosRequestConfig> => {
        try {
          // Get authenticated headers (JWT + X-Account)
          const authHeaders = await getAuthHeaders(
            authRef,
            {},
            config.targetAccountUuid
          );

          // Merge auth headers with existing headers
          Object.assign(requestConfig.headers, authHeaders);

          return requestConfig;
        } catch (error) {
          console.error("Error getting JWT token:", error);
          return Promise.reject(error);
        }
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - matches GraphQL error handling
    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        // Skip error handling in dev/localhost/amplify (same as GraphQL)
        if (shouldSkipErrorHandling()) {
          return Promise.reject(error);
        }

        const status = error.response?.status;

        // Handle 401/403 - same as GraphQL errorMiddleware
        if (status) {
          handleAuthError(
            status,
            authRef,
            logout,
            stopImpersonation,
            error.response?.data
          );
        }

        // Log errors (same pattern as GraphQL)
        const url = error.config?.url || "unknown";
        const method = error.config?.method?.toUpperCase() || "REQUEST";
        const opId = error.config?.headers?.["op-id"] as string | undefined;

        logWarning(error, `REST API Error: ${status || "Network Error"}`, {
          extra: { url, method, status },
        });

        logError(error, `REST API Error: ${error.message}`, {
          tags: {
            url,
            method,
            status: status?.toString() || "network_error",
            opId,
          },
          extra: {
            error: error.message,
            response: error.response?.data,
            config: {
              url: error.config?.url,
              method: error.config?.method,
            },
          },
        });

        return Promise.reject(error);
      }
    );

    return axiosInstance;
  }, [
    baseURL,
    config.domain,
    config.timeout,
    config.targetAccountUuid,
    headersString,
    logout,
    stopImpersonation,
    logWarning,
    logError,
  ]);

  return client;
};

export default useAxiosClient;
